import { FunctionComponent, useState } from 'react'
import { useMutation } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './mergeTwoStaffsDialog.module.scss'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { Merge } from '@mui/icons-material'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { StaffWithLinks } from '@/types/Staff.ts'
import { MERGE_TWO_STAFFS } from '@/modules/API/staff/staffHandler.ts'

interface MergeTwoStaffsDialogProps {
    open: boolean
    handleClose: () => void
    staffId: number
    staffName: string
    daycareId: number
    handleRefetchStaff: () => void
    staffsListData: StaffWithLinks[]
}

const MergeTwoStaffsDialog: FunctionComponent<MergeTwoStaffsDialogProps> = ({
    open = false,
    handleClose,
    staffId,
    daycareId,
    staffName,
    handleRefetchStaff,
    staffsListData
}: MergeTwoStaffsDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [selectedOldStaffId, setSelectedOldStaffId] = useState<string>('0')

    const [mergeTwoChilds] = useMutation(MERGE_TWO_STAFFS, {
        variables: {
            oldStaffId: Number(selectedOldStaffId),
            daycareId: daycareId,
            newStaffId: staffId
        },
        onCompleted: () => {
            handleRefetchStaff()

            showNotificationSuccess(t('mergeSuccess'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('mergeError'))
            handleClose()
        }
    })

    const selectOptions: SelectOptionsDeprecated = [
        {
            options: staffsListData
                .filter((staff: StaffWithLinks): boolean => staff.id !== staffId)
                .map((staff: StaffWithLinks): SelectOptionDeprecated => {
                    return {
                        value: staff.id.toString(),
                        name: staff.firstname && staff.lastname ? staff.firstname + ' ' + staff.lastname : staff.email
                    }
                })
                .sort((a: SelectOptionDeprecated, b: SelectOptionDeprecated): number => {
                    return a.name.localeCompare(b.name)
                })
        }
    ]

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('mergeStaff')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>
                    {t('whichStaffYouWantToMerge')}
                    <span className={styles.TextFamily}>{staffName}</span>
                    {' ?'}
                </p>
                <p className={styles.Text}>{t('noRollBack')}</p>
                <>
                    {selectOptions.length > 0 ? (
                        <div className={styles.InputContainer}>
                            <SelectUnique
                                selectOptions={selectOptions}
                                selectValue={selectedOldStaffId}
                                setSelectValue={setSelectedOldStaffId}
                                className={styles.Select}
                                label={t('staffs')}
                            />
                        </div>
                    ) : (
                        <ErrorMessage className={styles.Text} error={t('noStaff')} />
                    )}
                    <div className={styles.ButtonContainer}>
                        <ButtonTextIcon
                            type={'custom'}
                            customText={t('merge')}
                            customIcon={<Merge />}
                            onClick={mergeTwoChilds}
                            className={styles.Button}
                            disabled={selectedOldStaffId === '0'}
                        />
                        <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    </div>
                </>
            </DialogBody>
        </Dialog>
    )
}

export default MergeTwoStaffsDialog
